import {
  CommandDialog,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { TicketStatusBadge } from "@/components/ticket/ticket-status-badge";
import { router } from "@inertiajs/react";
import axios from "axios";
import {
  BarChart2,
  Briefcase,
  CheckSquare,
  LifeBuoy,
  Loader2,
  MessageSquare,
  Ticket,
  User,
  Users,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { urlFor } from "~/client";

type DealHit = {
  id: string;
  title: string;
  currentStage?: { name: string } | null;
};

type ContactHit = {
  id: number;
  name: string;
  email?: string | null;
  phone?: string | null;
};

type TicketHit = {
  id: number | string;
  title: string;
  status: string;
  numberTicket: number;
};

type SearchResults = {
  deals: DealHit[];
  contacts: ContactHit[];
  tickets: TicketHit[];
};

const EMPTY_RESULTS: SearchResults = { deals: [], contacts: [], tickets: [] };

/**
 * Command-K global: buscar/saltar a negocios, contactos y tareas + navegación
 * rápida. Montado una vez en WorkspaceShell; fan-out a los pickers JSON
 * existentes (deals.search / contacts.search / tickets.search) con debounce y
 * guard de respuesta stale.
 */
export function CommandPalette() {
  const { t } = useTranslation("chat");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResults>(EMPTY_RESULTS);
  const [loading, setLoading] = useState(false);
  const latestRequestId = useRef(0);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen((current) => !current);
      }
    };
    // El botón de búsqueda del header dispara este evento (evita acoplar el
    // shell al estado interno del palette).
    const handleOpenEvent = () => setOpen(true);
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("open-command-palette", handleOpenEvent);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("open-command-palette", handleOpenEvent);
    };
  }, []);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults(EMPTY_RESULTS);
      setLoading(false);
      return;
    }
  }, [open]);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      // Invalida también las respuestas en vuelo de la query anterior: sin el
      // bump, un fan-out lento repoblaría resultados stale tras el borrado.
      latestRequestId.current += 1;
      setResults(EMPTY_RESULTS);
      setLoading(false);
      return;
    }

    const requestId = ++latestRequestId.current;
    setLoading(true);
    const handle = setTimeout(async () => {
      // allSettled: que un buscador caído (p. ej. sin permiso de tickets) no
      // tumbe los resultados de los otros dos.
      const [deals, contacts, tickets] = await Promise.allSettled([
        axios.get<{ data: DealHit[] }>(urlFor("deals.search"), {
          params: { search: trimmed, per_page: 5 },
        }),
        axios.get<{ data: ContactHit[] }>(urlFor("contacts.search"), {
          params: { search: trimmed, per_page: 5 },
        }),
        axios.get<{ data: TicketHit[] }>(urlFor("tickets.search"), {
          params: { search: trimmed, limit: 5 },
        }),
      ]);
      if (requestId !== latestRequestId.current) {
        return;
      }
      setResults({
        deals: deals.status === "fulfilled" ? deals.value.data.data : [],
        contacts: contacts.status === "fulfilled" ? contacts.value.data.data : [],
        tickets: tickets.status === "fulfilled" ? tickets.value.data.data : [],
      });
      setLoading(false);
    }, 250);

    return () => clearTimeout(handle);
  }, [query, open]);

  const go = (url: string) => {
    setOpen(false);
    router.visit(url);
  };

  const hasQuery = query.trim().length >= 2;
  const hasResults =
    results.deals.length > 0 || results.contacts.length > 0 || results.tickets.length > 0;

  return (
    <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
      <CommandInput
        placeholder={t("command_palette.placeholder", {
          defaultValue: "Buscar negocios, contactos, tareas…",
        })}
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        {loading && (
          <div className="text-muted-foreground flex items-center justify-center gap-2 py-4 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("command_palette.searching", { defaultValue: "Buscando…" })}
          </div>
        )}
        {!loading && hasQuery && !hasResults && (
          <div className="text-muted-foreground py-4 text-center text-sm">
            {t("command_palette.no_results", { defaultValue: "Sin resultados" })}
          </div>
        )}

        {results.deals.length > 0 && (
          <CommandGroup heading={t("command_palette.deals", { defaultValue: "Negocios" })}>
            {results.deals.map((deal) => (
              <CommandItem
                key={`deal-${deal.id}`}
                value={`deal-${deal.id}`}
                onSelect={() => go(urlFor("deals.show", { dealId: Number(deal.id) }))}
              >
                <Briefcase className="mr-2 h-4 w-4" />
                <span className="min-w-0 flex-1 truncate">{deal.title}</span>
                {deal.currentStage?.name && (
                  <span className="text-muted-foreground ml-2 shrink-0 text-xs">
                    {deal.currentStage.name}
                  </span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {results.contacts.length > 0 && (
          <CommandGroup heading={t("command_palette.contacts", { defaultValue: "Contactos" })}>
            {results.contacts.map((contact) => (
              <CommandItem
                key={`contact-${contact.id}`}
                value={`contact-${contact.id}`}
                onSelect={() => go(urlFor("contacts.show", { contactId: Number(contact.id) }))}
              >
                <User className="mr-2 h-4 w-4" />
                <span className="min-w-0 flex-1 truncate">{contact.name}</span>
                {(contact.email || contact.phone) && (
                  <span className="text-muted-foreground ml-2 shrink-0 truncate text-xs">
                    {contact.email ?? contact.phone}
                  </span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {results.tickets.length > 0 && (
          <CommandGroup heading={t("command_palette.tickets", { defaultValue: "Tareas" })}>
            {results.tickets.map((ticket) => (
              <CommandItem
                key={`ticket-${ticket.id}`}
                value={`ticket-${ticket.id}`}
                onSelect={() =>
                  go(urlFor("tickets.show", { numberTicket: Number(ticket.numberTicket) }))
                }
              >
                <Ticket className="mr-2 h-4 w-4" />
                <span className="text-muted-foreground mr-2 shrink-0 text-xs tabular-nums">
                  #{ticket.numberTicket}
                </span>
                <span className="min-w-0 flex-1 truncate">{ticket.title}</span>
                <TicketStatusBadge status={String(ticket.status)} />
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {!hasQuery && (
          <>
            <CommandSeparator />
            <CommandGroup heading={t("command_palette.go_to", { defaultValue: "Ir a" })}>
              <CommandItem value="nav-chat" onSelect={() => go(urlFor("chat.index"))}>
                <MessageSquare className="mr-2 h-4 w-4" />
                {t("command_palette.nav_chat", { defaultValue: "Chat" })}
              </CommandItem>
              <CommandItem value="nav-deals" onSelect={() => go(urlFor("deals.index"))}>
                <Briefcase className="mr-2 h-4 w-4" />
                {t("command_palette.nav_deals", { defaultValue: "Negocios" })}
              </CommandItem>
              <CommandItem value="nav-contacts" onSelect={() => go(urlFor("contacts.index"))}>
                <Users className="mr-2 h-4 w-4" />
                {t("command_palette.nav_contacts", { defaultValue: "Contactos" })}
              </CommandItem>
              <CommandItem value="nav-tickets" onSelect={() => go(urlFor("tickets.index"))}>
                <CheckSquare className="mr-2 h-4 w-4" />
                {t("command_palette.nav_tickets", { defaultValue: "Tareas" })}
              </CommandItem>
              <CommandItem
                value="nav-support"
                onSelect={() => go(`${urlFor("tickets.index")}?type=support`)}
              >
                <LifeBuoy className="mr-2 h-4 w-4" />
                {t("sidebar.support", { ns: "home_common", defaultValue: "Soporte" })}
              </CommandItem>
              <CommandItem value="nav-reports" onSelect={() => go(urlFor("reports.index"))}>
                <BarChart2 className="mr-2 h-4 w-4" />
                {t("command_palette.nav_reports", { defaultValue: "Reportes" })}
              </CommandItem>
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
